import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants';
import { ff } from '@atlassian/jira-feature-flagging';
import type { TransformedGiraData } from '@atlassian/jira-issue-gira-transformer-types';
import {
	type TransformedDataToLegacyGraphql,
	type TransformedDataToLegacyGira,
	PROJECT_ACTIVE,
	type JiraSettingsAGG,
} from '@atlassian/jira-issue-shared-types';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import type { TransformDataOld, TransformDataNew } from '../index';

export type CombinedData = TransformedDataToLegacyGraphql & TransformDataOld;

const projectDefaults = {
	projectId: '',
	projectName: '',
	projectKey: '',
	projectStatus: PROJECT_ACTIVE,
	projectType: SOFTWARE_PROJECT,
	isSimplified: false,
};

export const combineResponseData = (
	aggLegacyGraphqlData: TransformedDataToLegacyGraphql | null,
	aggLegacyGiraData: TransformedDataToLegacyGira | null,
	giraData: TransformedGiraData,
): CombinedData => {
	if (!aggLegacyGraphqlData || !aggLegacyGiraData) {
		// The transformers did not return anything - this happens if the data is missing for some reason
		// We should never fall into this case but its here for type checking
		const message = 'Failed to transform issue data from AGG, transformers returned empty value';
		throw new Error(message);
	}

	const jiraSettings = {
		jiraSettings: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			...(aggLegacyGiraData.jiraSettings as JiraSettingsAGG),
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			newIssueViewTransitionSettings: (giraData as TransformDataNew).jiraSettings
				?.newIssueViewTransitionSettings,
		},
	};

	const attachments = ff('issue.details.gira-agg-migration-attachments_ecebe')
		? {
				attachments: {
					totalCount: giraData.totalAttachmentsCount || 0,
					nodes: aggLegacyGiraData.attachments || [],
					deletableCount: 0,
				},
			}
		: {
				attachments: giraData.attachments,
			};

	if (
		ff('ken-432-missing-project-key-log_hr7zv') &&
		aggLegacyGraphqlData?.project &&
		!aggLegacyGraphqlData?.project?.projectType
	) {
		getAnalyticsWebClientPromise().then((client) => {
			client.getInstance().sendTrackEvent({
				action: 'project-type-investigation',
				attributes: {
					message: 'projectType missing in project data',
				},
				actionSubject: 'project-context',
				source: 'issue-view',
			});
		});
	}

	return {
		...giraData,
		...aggLegacyGraphqlData,
		...aggLegacyGiraData,
		...jiraSettings,
		...attachments,
		project: {
			...projectDefaults,
			...aggLegacyGraphqlData?.project,
			issueTypes: giraData.issueTypes,
		},
	};
};
